import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, computed,
  ContentChild,
  DoCheck, effect,
  EventEmitter,
  inject, input,
  Input,
  Output,
  TemplateRef
} from '@angular/core';
import {CustomLoaderService} from '../../../services/custom-loader.service';
import {
  CellTransformerPipe,
  GenericSortState,
  GenericTableFooterTemplateDirective,
  GenericTableHeaderTemplateDirective,
  GenericTableRowActionTemplateDirective,
  GenericTableRowTemplateDirective
} from "../../index";
import {NgxUiLoaderModule} from "ngx-ui-loader";
import {CommonModule, NgOptimizedImage} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {SortIconComponent} from "../sort-icon/sort-icon.component";
import {GenericTableEmptyTemplateDirective} from "./directive/generic-table-empty-template.directive";

type NonFunctionKeys<T> = { [K in keyof T]: T[K] extends Function ? never : K }[keyof T];

export type CellTransformerFn<TValue = any, TRow = any, R = any> = (options: {
  value?: TValue,
  rowData: TRow,
  index: number
}) => R;


/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
type ColumnConfig<T> = {
  column:'action_cell' | 'computed_cell' | NonFunctionKeys<T> | string,
  sort?: boolean,
  cellType?: 'default' | 'html',
  cellRender?: CellTransformerFn<any, T>
}


/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export type GenericTableColumn<T = any> = {
  [property: string]: any,

  label: string,
  class?: string[] | string,
  style?: { [property: string]: any },
  enabled?: boolean,
  config?: ColumnConfig<T>
}


@Component({
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  standalone: true,
  imports: [
    NgxUiLoaderModule,
    CommonModule,
    TranslateModule,
    SortIconComponent,
    NgOptimizedImage,
    CellTransformerPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export class GenericTableComponent<TItem extends object> implements DoCheck {
  //DI
  private customLoaderService = inject(CustomLoaderService);
  private cdr = inject(ChangeDetectorRef);

  loading = input<boolean, unknown>(false, {transform: booleanAttribute});
  data = input.required<TItem[]>();

  @Input({required: false}) columns: GenericTableColumn[] = [];
  @Input({required: false}) trackBy?: string;

  @Output() sortChange: EventEmitter<string> = new EventEmitter<string>();

  @ContentChild(GenericTableHeaderTemplateDirective, {read: TemplateRef})
  headersTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableRowTemplateDirective, {read: TemplateRef})
  rowTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableRowActionTemplateDirective, {read: TemplateRef})
  rowActionTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableFooterTemplateDirective, {read: TemplateRef})
  footerTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableEmptyTemplateDirective, {read: TemplateRef})
  emptyTableTemplateRef: TemplateRef<any> | null = null;

  @Input() headerClass: string[] | string;
  @Input() headerStyle: { [klass: string]: any };

  @Input() sortState: GenericSortState;

  @Input({transform: booleanAttribute}) striped: boolean = false;
  @Input({transform: booleanAttribute}) bordered: boolean = false;
  @Input({transform: booleanAttribute}) rounded: boolean = false;
  @Input({transform: booleanAttribute}) flushStyles: boolean = false;
  @Input({transform: booleanAttribute}) showRowBorder: boolean = false;
  @Input({transform: booleanAttribute}) rowHoverable: boolean = true;
  @Input({transform: booleanAttribute}) showRowBorderDashed: boolean = false;
  @Input({transform: booleanAttribute}) darkMode: boolean = false;

  @Input({transform: booleanAttribute}) showSpinner: boolean = true;


  //no data options
  @Input() noDataMessage: string = 'Lo sentimos no hay información disponible.';
  @Input({transform: booleanAttribute}) noDataShowImg: boolean = true;

  @Input({transform: booleanAttribute}) showTableIfEmpty: boolean = true;
  @Input({transform: booleanAttribute}) detectChanges: boolean = false;

  protected componentLoaderID: string = 'generic-table-loaderId' + self.crypto.randomUUID();

  protected tableHasItems = computed(()=> {
    const data = this.data();
    if (undefined === data || null === data) {
      return false;
    }

    return data.length > 0;
  });

  constructor() {
    effect(() => {
      this.toggleBlockTableUI(this.loading())
    });
  }

  ngDoCheck(): void {
    if (this.detectChanges) {
      this.cdr.markForCheck();
    }
  }

  toggleBlockTableUI(state: boolean | undefined | null) {

    if (!this.showSpinner) return;

    if (state) {
      this.customLoaderService.startLoader(this.componentLoaderID)
      return;
    }

    this.customLoaderService.stopLoader(this.componentLoaderID)
  }

  sortingEmitter(column: string) {
    this.sortChange.emit(column)
  }

  trackByFn(_: number, item: any) {
    if (this.trackBy) {
      return item[this.trackBy]
    }

    if (item.hasOwnProperty('id')) {
      return item.id
    }

    return item;
  }

}
